import { useDialog } from "../hooks/useDialog";
import { memo, useRef } from "react";
import type { BuildingProperties } from "../map/layers";
import type { BuildingAttributes, BuildingTenant } from "../data/buildingAttributes";
import { StreetViewThumb } from "./StreetViewThumb";

interface Props {
  building: BuildingProperties | null;
  attrs?: BuildingAttributes | null;
  /** [lng, lat] of the picked building — drives the ground-truth Street View thumb. */
  coord?: [number, number] | null;
  /** Modeled people-in-building right now (scenario tier) — from lib/opsModel via App. */
  occupancy?: { occupied: number; capacity: number; use: string } | null;
  onClose: () => void;
}

const CATEGORY_LABEL: Record<BuildingTenant["category"], string> = {
  retail: "Retail", "f&b": "F&B", service: "Service", office: "Office",
  academic: "Academic", telecom: "Telecom", other: "Other",
};

/**
 * Right-anchored card showing the picked building's metadata, plus the
 * space + tenancy panel the Office of Physical Systems asked for: which
 * rooms are in use vs. vacant, and who rents the commercial space.
 * Opens on click (not hover) — buildings are everywhere on campus, so a
 * hover tooltip would never settle. Closes on backdrop click or ESC.
 */
function BuildingCardImpl({ building, attrs, coord, occupancy, onClose }: Props) {
  const dialogRef = useRef<HTMLElement>(null);
  useDialog(dialogRef, !!building, onClose, false);
  if (!building) return null;
  const name = building.nameEn || building.name || building.nameTh || "Untitled building";
  const altName = building.nameTh && building.nameTh !== name ? building.nameTh : null;

  const rooms = attrs?.rooms;
  const occPct = rooms && rooms.total > 0 ? Math.round((rooms.occupied / rooms.total) * 100) : null;
  const vacant = rooms ? Math.max(0, rooms.total - rooms.occupied) : null;
  const isSample = attrs?.source === "pmcu-sample";

  return (
    <aside ref={dialogRef} className="building-card" role="dialog" aria-label={`Building: ${name}`}>
      <header className="building-card-head">
        <div>
          <span className="eyebrow mono">CHULA · BUILDING</span>
          <h3 className="building-card-title">{name}</h3>
          {altName && <div className="building-card-alt">{altName}</div>}
        </div>
        <button onClick={onClose} aria-label="Close" className="building-card-close mono">
          ESC
        </button>
      </header>
      <dl className="building-card-meta mono">
        {building.building && building.building !== "yes" && (
          <>
            <dt>TYPE</dt>
            <dd>{building.building}</dd>
          </>
        )}
        {building.levels != null && (
          <>
            <dt>LEVELS</dt>
            <dd>{building.levels}</dd>
          </>
        )}
        {building.height != null && (
          <>
            <dt>HEIGHT</dt>
            <dd>{building.height} m</dd>
          </>
        )}
        {building.operator && (
          <>
            <dt>OPERATOR</dt>
            <dd>{building.operator}</dd>
          </>
        )}
        {occupancy && (
          <>
            <dt title="Modeled from floor area × academic-calendar tempo × hour of day — scenario tier until Wi-Fi association or keycard counts are wired">OCCUPANCY · SIM</dt>
            <dd>
              {occupancy.occupied.toLocaleString()} / {occupancy.capacity.toLocaleString()}
              <span className="caption" style={{ marginLeft: 6, color: "var(--text-3)" }}>
                {Math.round((occupancy.occupied / occupancy.capacity) * 100)}% · {occupancy.use}
              </span>
            </dd>
          </>
        )}
        <dt>OSM</dt>
        <dd className="mono">{building.id}</dd>
      </dl>

      {/* ── Ground truth: Google Street View at this location ── */}
      {coord && (
        <div className="building-card-section">
          <span className="eyebrow mono">STREET LEVEL</span>
          <StreetViewThumb coord={coord} />
        </div>
      )}

      {/* ── Space & tenancy ─────────────────────────────────────────────── */}
      <section className="building-space" aria-label="Space and tenancy">
        <div className="building-space-head">
          <span className="eyebrow mono">SPACE &amp; TENANCY</span>
          {isSample && <span className="building-space-tag mono" title="Illustrative sample — replaced by the live PMCU feed">SAMPLE</span>}
        </div>

        {!attrs && (
          <p className="building-space-empty mono">Awaiting PMCU feed — room &amp; tenancy data plugs in here.</p>
        )}

        {rooms && (
          <div className="building-occ">
            <div className="building-occ-row mono">
              <span><strong>{rooms.occupied}</strong> / {rooms.total} rooms in use</span>
              <span className="building-occ-pct">{occPct}%</span>
            </div>
            <div className="building-occ-bar" role="img" aria-label={`${occPct} percent of rooms in use`}>
              <span style={{ width: `${occPct}%` }} />
            </div>
            <div className="building-occ-sub mono">{vacant} vacant</div>
          </div>
        )}

        {attrs?.tenants && attrs.tenants.length > 0 && (
          <ul className="building-tenants">
            {attrs.tenants.map((t, i) => (
              <li key={i} className="building-tenant">
                <div className="building-tenant-top">
                  <span className="building-tenant-name">{t.name}</span>
                  <span className="building-tenant-cat mono">{CATEGORY_LABEL[t.category]}</span>
                </div>
                <div className="building-tenant-sub mono">
                  {t.space && <span>{t.space}</span>}
                  {t.leaseEnd && (
                    <span className={t.status === "expiring" ? "building-tenant-expiring" : ""}>
                      {t.status === "expiring" ? "expires " : "lease to "}{t.leaseEnd}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {attrs?.asOf && <div className="building-space-asof mono">as of {attrs.asOf}</div>}
      </section>
    </aside>
  );
}

export const BuildingCard = memo(BuildingCardImpl);
